/**
 * Invoice Cron Job Helper Functions
 *
 * Pure utility functions extracted from the auto monthly invoice cron route
 * for testability and reuse.
 *
 * TIMEZONE NOTE: Vercel cron runs in UTC. All date checks (day-of-month matching,
 * billing month resolution) must convert to the gym's IANA timezone first.
 * Use getNowInTimezone() / getOrgTimezone() to get timezone-aware dates.
 */

import { formatDateOnly } from '@/utils/formatters/dateUtils';
import { createLogger } from '@/utils/logger/devLogger';
import { DEFAULT_SERVER_TIMEZONE } from '@/utils/timezone/serverLocalDate';
import type { AutoInvoiceEffectivePolicy } from '@/types/autoInvoicing';
import type { SupabaseClient } from '@supabase/supabase-js';

const logger = createLogger('InvoiceCronHelpers');

/**
 * Maximum number of missed months to catch up on in a single cron run.
 * Prevents accidental mass-invoice creation from very old data.
 */
export const MAX_CATCHUP_MONTHS = 3;

/**
 * Number of days past the canonical half-2 send date during which the cron
 * will still recover (write) a missing half-2 invoice. Keeps a single Vercel
 * outage or weekend deploy freeze from silently halving a gym's revenue.
 *
 * Outside this window, the gym must use the manual "Generate Monthly Tuition"
 * tool (Team Tuition page) to backfill.
 */
export const HALF2_RECOVERY_GRACE_DAYS = 7;

/**
 * Extract YYYY-MM from a Date object.
 */
export function yyyyMmFromDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/**
 * Add (or subtract) months from a date, clamping day-of-month to the max of the target month.
 */
export function addMonths(date: Date, deltaMonths: number): Date {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  const day = d.getDate();
  const next = new Date(y, m + deltaMonths, 1);
  const maxDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
  next.setDate(Math.min(day, maxDay));
  return next;
}

/**
 * Timezone-safe date formatting alias (delegates to formatDateOnly).
 */
export const yyyyMmDd = formatDateOnly;

/**
 * Round a number to 2 decimal places (currency precision).
 * Re-exported from billingConstants for backwards compatibility — new code should import from billingConstants directly.
 */
export { roundCurrency } from '@/utils/billing/billingConstants';

/**
 * Compute the list of billing months to generate invoices for.
 * Returns the current month plus any missed months (up to MAX_CATCHUP_MONTHS).
 *
 * @param lastBilledMonth - The most recent month already billed (YYYY-MM), or null if none
 * @param enabledAtDate - When auto-invoicing was enabled (used as starting point if no prior invoices)
 * @param currentMonth - The current billing month (YYYY-MM)
 * @returns Array of YYYY-MM strings to bill for, oldest first
 */
export function computeBillingMonthsToProcess(params: {
  lastBilledMonth: string | null;
  enabledAtDate: string | null;
  currentMonth: string;
}): string[] {
  const { lastBilledMonth, enabledAtDate, currentMonth } = params;

  // Determine the starting reference month
  let startAfterMonth: string | null = lastBilledMonth;

  if (!startAfterMonth && enabledAtDate) {
    // No prior invoices - start from the month auto-invoicing was enabled
    // We subtract 1 month so the enabled month itself is included
    const enabledDate = new Date(enabledAtDate);
    const prevMonth = new Date(enabledDate.getFullYear(), enabledDate.getMonth() - 1, 1);
    startAfterMonth = yyyyMmFromDate(prevMonth);
  }

  if (!startAfterMonth) {
    // No prior invoices and no enabled_at timestamp - just bill current month
    return [currentMonth];
  }

  // Generate all months from (startAfterMonth + 1) through currentMonth
  const months: string[] = [];
  const [startY, startM] = startAfterMonth.split('-').map(Number);
  const [curY, curM] = currentMonth.split('-').map(Number);

  let y = startY;
  let m = startM;

  // Advance one month past the last billed month
  m += 1;
  if (m > 12) { m = 1; y += 1; }

  while (y < curY || (y === curY && m <= curM)) {
    months.push(`${y}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }

  // Safety cap: only catch up the most recent MAX_CATCHUP_MONTHS months
  if (months.length > MAX_CATCHUP_MONTHS) {
    const skipped = months.length - MAX_CATCHUP_MONTHS;
    logger.warn(`Catch-up capped: skipping ${skipped} older months, processing only last ${MAX_CATCHUP_MONTHS}`, {
      allMonths: months,
      processing: months.slice(-MAX_CATCHUP_MONTHS),
    });
    return months.slice(-MAX_CATCHUP_MONTHS);
  }

  // If current month isn't in the list (shouldn't happen), add it
  if (months.length === 0) {
    months.push(currentMonth);
  }

  return months;
}

/**
 * Add (or subtract) whole months to a YYYY-MM string, returning YYYY-MM.
 * Pure calendar arithmetic (no timezone involved — operates on the month label).
 */
export function addMonthsToYyyyMm(yyyyMm: string, delta: number): string {
  const [y, m] = yyyyMm.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return yyyyMmFromDate(d);
}

/**
 * Return the latest (max) YYYY-MM among the inputs, ignoring null/undefined.
 * Lexicographic comparison is correct for zero-padded YYYY-MM strings.
 */
export function maxYyyyMm(...months: Array<string | null | undefined>): string | null {
  const valid = months.filter((x): x is string => Boolean(x));
  if (valid.length === 0) return null;
  return valid.reduce((a, b) => (a >= b ? a : b));
}

/**
 * Latest of several ISO timestamps, ignoring null/undefined/unparseable.
 * Used to pick the newest of the master "turned on at" stamp and a per-type
 * stamp so the resume-forward floor below is the LATEST re-enable.
 */
export function latestIsoTimestamp(...stamps: Array<string | null | undefined>): string | null {
  let best: { iso: string; ms: number } | null = null;
  for (const iso of stamps) {
    if (!iso) continue;
    const ms = Date.parse(iso);
    if (Number.isNaN(ms)) continue;
    if (!best || ms > best.ms) best = { iso, ms };
  }
  return best?.iso ?? null;
}

/**
 * Resume-forward floor (plan 2026-09-12-001 KTD4 / R7): drop every billing
 * month EARLIER than the gym-local month of `stampIso`. Turning the master
 * switch or a per-type box back on bills the turn-on month and later — never
 * the months it sat off. A null stamp (gym born with invoicing on, or the box
 * never re-checked) drops nothing, so the catch-up for a missed nightly run
 * while everything stayed on is untouched.
 *
 * Applied AFTER the enumerators (`computeBillingMonthsToProcess`,
 * `expandBillingMonthsForSplit`, `computeEnrollmentCatchupMonths`), because
 * `enabledAtDate` inside them is only a first-run anchor and is ignored the
 * moment a prior invoice exists.
 */
export function dropBillingMonthsBefore(params: {
  months: string[];
  stampIso: string | null | undefined;
  timezone: string;
}): string[] {
  const { months, stampIso, timezone } = params;
  if (!stampIso) return months;
  const floor = yyyyMmInTimezone(stampIso, timezone);
  return months.filter((m) => m >= floor);
}

/**
 * Resolve the YYYY-MM month label of a timestamp in a given IANA timezone.
 * Used to anchor an enrollment's catch-up window to the gym-local month of its
 * creation / the org's enable timestamp (parity with billing, which is gym-local).
 *
 * An invalid/unknown timezone falls back to the UTC month (logged) instead of
 * throwing — one bad gymlocations.timezone row must not abort the whole cron
 * section for every org.
 */
export function yyyyMmInTimezone(date: Date | string, timezone: string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  try {
    const fmt = new Intl.DateTimeFormat('en-US', { timeZone: timezone, year: 'numeric', month: '2-digit' });
    const parts = fmt.formatToParts(d);
    const y = parts.find((p) => p.type === 'year')?.value ?? '0';
    const m = parts.find((p) => p.type === 'month')?.value ?? '00';
    return `${y}-${m}`;
  } catch (err) {
    logger.warn('yyyyMmInTimezone: invalid timezone, falling back to UTC month', { timezone, err });
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  }
}

/**
 * Compute the billing months to (re)bill for a single program enrollment, mirroring
 * the team/division catch-up so a skipped enrollment month (transient error, void, or
 * config change) is caught up on a later send-day run instead of being silently dropped
 * (GH #1812). Returns YYYY-MM strings oldest-first, capped at MAX_CATCHUP_MONTHS.
 *
 * Anchoring rules:
 * - `lastBilledMonth` is the newest NON-VOID `enrollment:{id}:%` month (the caller MUST
 *   exclude void — see the #1813 widened unique index note in the cron route). When set,
 *   we bill (lastBilledMonth + 1) .. currentMonth.
 * - When there is no prior non-void enrollment invoice, the first cron-billed month is the
 *   LATEST of: the month AFTER `firstBilledMonth` (the signup invoice already covered that
 *   month), the enrollment's own creation month, and the org's auto-invoice enable month —
 *   never earlier than any of these. If the anchor lands past the current month (e.g. a
 *   signup this month), there is nothing to catch up and we return [].
 * - With no anchor information at all we fall back to billing just the current month.
 *
 * The per-month `candidateMonth === firstBilledMonth` skip still lives in the caller as
 * belt-and-suspenders; a NULL firstBilledMonth never excludes a month here.
 *
 * BY DESIGN: interior voided months (voided with a LATER non-void month already billed)
 * are unreachable — the window starts after the newest non-void month. Live interior voids
 * were manual staff repairs under different source_keys; auto re-billing would double-charge.
 * Only TAIL voids (nothing non-void after them) auto-repair.
 */
export function computeEnrollmentCatchupMonths(params: {
  lastBilledMonth: string | null;
  firstBilledMonth: string | null;
  enrollmentCreatedMonth: string | null;
  orgEnabledMonth: string | null;
  currentMonth: string;
  /**
   * #4724 (P4): the earliest month this enrollment may be billed — the LATER of
   * the month promised at checkout (`program_enrollments.billing_starts_month`)
   * and the class's live first-class month. The window never starts before
   * it, whether or not a prior month was billed: moving the class date earlier
   * never bills a month the family was told was free, and moving it later
   * never bills a month with no class. Null/absent ⇒ no floor (every
   * enrollment made on or after the first class, and every undated class).
   */
  billingFloorMonth?: string | null;
}): string[] {
  const { lastBilledMonth, firstBilledMonth, enrollmentCreatedMonth, orgEnabledMonth, currentMonth } = params;
  const billingFloorMonth = params.billingFloorMonth || null;
  // A floor still ahead of this run ⇒ nothing to bill yet, billed-before or not
  // (the enumerator otherwise falls back to [currentMonth] when its boundary
  // reaches the current month).
  if (billingFloorMonth && billingFloorMonth > currentMonth) return [];

  if (lastBilledMonth) {
    // The floor can only move the boundary FORWARD (a promised month is never
    // later than a month already billed; a moved-later class date can be).
    const boundary = maxYyyyMm(lastBilledMonth, billingFloorMonth ? addMonthsToYyyyMm(billingFloorMonth, -1) : null) as string;
    return computeBillingMonthsToProcess({ lastBilledMonth: boundary, enabledAtDate: null, currentMonth });
  }

  const anchor = maxYyyyMm(
    firstBilledMonth ? addMonthsToYyyyMm(firstBilledMonth, 1) : null,
    enrollmentCreatedMonth,
    orgEnabledMonth,
    billingFloorMonth,
  );

  if (!anchor) {
    // No anchor info — bill only the current month.
    return [currentMonth];
  }

  if (anchor > currentMonth) {
    // The first billable month is in the future (e.g. a signup this month whose signup
    // invoice already covers the current month) — nothing to catch up.
    return [];
  }

  // Bill anchor .. currentMonth. Reuse the tested enumerator by treating the month BEFORE
  // the anchor as the synthetic "last billed" boundary (exact, avoids Date-parse tz drift).
  return computeBillingMonthsToProcess({
    lastBilledMonth: addMonthsToYyyyMm(anchor, -1),
    enabledAtDate: null,
    currentMonth,
  });
}

/**
 * Compute the due date for an auto-generated invoice based on the org/division policy.
 */
export function computeDueDate(params: {
  // `auto_invoice_due_day_next_month` is REQUIRED (#2663). It was briefly optional
  // so narrow fixtures stayed short, and that immediately cost us: two cron paths
  // hand-build this object field-by-field and silently omitted the flag, which
  // would have billed an opted-in gym's class-enrollment invoices born-overdue
  // (marked overdue and auto-charged the same day). The compiler is the only
  // thing standing between a new call site and that bug — keep it required.
  policy: Pick<
    AutoInvoiceEffectivePolicy,
    | 'auto_invoice_due_date_mode'
    | 'auto_invoice_due_days_after_issue'
    | 'auto_invoice_due_day'
    | 'auto_invoice_due_day_next_month'
  >;
  billingMonth: string; // YYYY-MM
  invoiceDate: Date;
}): string {
  const { policy, billingMonth, invoiceDate } = params;

  if (policy.auto_invoice_due_date_mode === 'days_after_issue') {
    const due = new Date(invoiceDate);
    due.setDate(due.getDate() + (policy.auto_invoice_due_days_after_issue || 0));
    return yyyyMmDd(due);
  }

  // fixed_day_of_month
  const dueDay = policy.auto_invoice_due_day || 1;
  // #2663 — "due on the Nth of the FOLLOWING month". Rolls the MONTH LABEL via
  // addMonthsToYyyyMm and re-attaches the day, so the day-of-month is never
  // subjected to date arithmetic: due day is capped at 28 elsewhere, so there is
  // no short-February clamping to get wrong. (addMonthsToYyyyMm does construct a
  // Date internally, but only from a day-1 anchor read back with the matching
  // local getters, so it cannot shift the label.) Dec -> Jan rolls the year.
  const targetMonth = addMonthsToYyyyMm(billingMonth, policy.auto_invoice_due_day_next_month ? 1 : 0);
  const [yyyy, mm] = targetMonth.split('-');
  return `${yyyy}-${mm}-${String(dueDay).padStart(2, '0')}`;
}

/**
 * Suggested default due date for the manual "Generate Monthly Tuition" tool (#2020).
 *
 * Delegates to computeDueDate so a manually generated invoice defaults to the
 * same due date the auto-invoicing cron would have given it — a mid-month
 * athlete joins the rest of the team's billing cadence instead of the old
 * hardcoded today+30 default.
 *
 * One intentional divergence from the cron: when the policy date is already in
 * the past (generating July on the 20th for a gym due on the 18th, or
 * backfilling a prior month), the suggestion clamps to today. A past due date
 * would make the invoice born-overdue and start the late-fee clock on a family
 * that did nothing wrong; due-today still gets auto-pay to charge it on the
 * next scheduled run.
 */
export function computeSuggestedManualDueDate(params: {
  // `auto_invoice_due_day_next_month` is REQUIRED (#2663). It was briefly optional
  // so narrow fixtures stayed short, and that immediately cost us: two cron paths
  // hand-build this object field-by-field and silently omitted the flag, which
  // would have billed an opted-in gym's class-enrollment invoices born-overdue
  // (marked overdue and auto-charged the same day). The compiler is the only
  // thing standing between a new call site and that bug — keep it required.
  policy: Pick<
    AutoInvoiceEffectivePolicy,
    | 'auto_invoice_due_date_mode'
    | 'auto_invoice_due_days_after_issue'
    | 'auto_invoice_due_day'
    | 'auto_invoice_due_day_next_month'
  >;
  billingMonth: string; // YYYY-MM
  todayYmd: string; // org-local YYYY-MM-DD
}): { dueDate: string; clampedToToday: boolean } {
  const { policy, billingMonth, todayYmd } = params;
  const policyDate = computeDueDate({
    policy,
    billingMonth,
    // Local midnight so days_after_issue arithmetic can't be shifted a day by
    // the server's UTC offset.
    invoiceDate: new Date(`${todayYmd}T00:00:00`),
  });
  if (policyDate < todayYmd) {
    return { dueDate: todayYmd, clampedToToday: true };
  }
  return { dueDate: policyDate, clampedToToday: false };
}

// ---------------------------------------------------------------------------
// Timezone-aware date helpers for cron jobs running on UTC servers (Vercel)
// ---------------------------------------------------------------------------

// Canonical server-default timezone lives in serverLocalDate.ts; alias it here
// so the existing DEFAULT_TIMEZONE usages below stay unchanged (single source
// of truth for the 'America/New_York' fallback).
const DEFAULT_TIMEZONE = DEFAULT_SERVER_TIMEZONE;

/**
 * The org's timezone from its oldest gym location. Rejects when the read fails;
 * no location or no timezone set gives America/New_York.
 */
export async function getOrgTimezoneOrThrow(
  supabase: SupabaseClient,
  organizationId: string,
): Promise<string> {
  const { data, error } = await supabase
    .from('gymlocations')
    .select('timezone')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.timezone || DEFAULT_TIMEZONE;
}

/** getOrgTimezoneOrThrow, but a failed read also gives America/New_York (logged). */
export async function getOrgTimezone(
  supabase: SupabaseClient,
  organizationId: string,
): Promise<string> {
  try {
    return await getOrgTimezoneOrThrow(supabase, organizationId);
  } catch (err) {
    logger.warn('Failed to fetch org timezone, using default', { organizationId, err });
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Convert the current UTC time to a Date-like object in the given IANA timezone.
 * Returns { year, month (1-based), day, hour, dateStr (YYYY-MM-DD), billingMonth (YYYY-MM) }.
 *
 * Uses Intl.DateTimeFormat (no external libraries) to resolve the local date parts.
 */
export function getNowInTimezone(timezone: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  dateStr: string;
  billingMonth: string;
} {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(now);
  const get = (type: string) => parts.find(p => p.type === type)?.value || '0';

  const year = Number(get('year'));
  const month = Number(get('month'));
  const day = Number(get('day'));
  const hour = Number(get('hour'));

  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');

  return {
    year,
    month,
    day,
    hour,
    dateStr: `${year}-${mm}-${dd}`,
    billingMonth: `${year}-${mm}`,
  };
}

/**
 * Batch-fetch timezones for multiple organizations in a single query.
 * Returns a Map<orgId, IANA timezone string>.
 * Orgs without a location default to America/New_York.
 */
export async function getOrgTimezones(
  supabase: SupabaseClient,
  organizationIds: string[],
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (organizationIds.length === 0) return result;

  try {
    // Fetch the oldest location per org (primary) with its timezone
    const { data, error } = await supabase
      .from('gymlocations')
      .select('organization_id, timezone, created_at')
      .in('organization_id', organizationIds)
      .order('created_at', { ascending: true });

    if (error) {
      logger.warn('Failed to batch-fetch org timezones, using defaults', { error: error.message });
      organizationIds.forEach(id => result.set(id, DEFAULT_TIMEZONE));
      return result;
    }

    // Pick the first (oldest) location per org
    for (const row of data || []) {
      if (!result.has(row.organization_id) && row.timezone) {
        result.set(row.organization_id, row.timezone);
      }
    }
  } catch (err) {
    logger.warn('Exception batch-fetching org timezones, using defaults', { err });
  }

  // Fill in defaults for any orgs that didn't have a location with a timezone
  for (const id of organizationIds) {
    if (!result.has(id)) {
      result.set(id, DEFAULT_TIMEZONE);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Semi-monthly split helpers (GH issue #555)
// ---------------------------------------------------------------------------

/**
 * Parse YYYY-MM into { year, month } where month is 1-indexed.
 */
function parseBillingMonth(billingMonth: string): { year: number; month: number } {
  const [y, m] = billingMonth.split('-').map(Number);
  return { year: y, month: m };
}

/** A calendar date with a 1-indexed month. */
type CalendarDate = { year: number; month: number; day: number };

/**
 * Whole calendar days from `from` to `to`, positive when `to` is later.
 *
 * Counts DATES, not elapsed time, so a daylight-saving change inside the range
 * cannot make the answer 6.96 or 7.04 days. Subtracting two local-midnight
 * `Date` objects does exactly that, and both half-2 grace windows are inclusive
 * `<= HALF2_RECOVERY_GRACE_DAYS` comparisons — so a FALL-BACK inside the window
 * (the extra hour: Oct 29 to Nov 5 measures 7.0417) pushed the last eligible day
 * out of it in a DST zone while leaving it in for a zone without DST. A
 * spring-forward is harmless in the other direction — it measures 6.958, which
 * is already inside an inclusive window. Not a production defect: Vercel runs
 * UTC, where local midnights are an exact 86,400,000 ms apart (#3614).
 */
function calendarDaysBetween(from: CalendarDate, to: CalendarDate): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const a = Date.UTC(from.year, from.month - 1, from.day);
  const b = Date.UTC(to.year, to.month - 1, to.day);
  return Math.round((b - a) / msPerDay);
}

/**
 * The canonical send date for half-2 of a given billing month, expressed as a
 * Date object in calendar (not timezone-shifted) terms.
 *
 * half1SendDate (notional) = first day of billingMonth + (send_on_day - 1)
 * half2SendDate = half1SendDate + offset_days
 *
 * When (send_on_day - 1) + offset_days exceeds the days in billingMonth, the
 * result naturally lands in the next calendar month. The half-2 invoice still
 * attributes to the original billing month.
 */
export function computeHalf2SendDate(params: {
  billingMonth: string; // YYYY-MM
  sendOnDay: number; // 1-28
  offsetDays: number; // 1-15
}): Date {
  const { year, month } = parseBillingMonth(params.billingMonth);
  // Start at the first of the billing month, then add (send_on_day - 1) + offset.
  // JS Date arithmetic rolls past month-end into the next month automatically.
  return new Date(year, month - 1, params.sendOnDay + params.offsetDays);
}

/**
 * Compute half-2's due date by offsetting from half-1's due date by the same
 * split offset. This decouples half-2 from `auto_invoice_due_day` /
 * `auto_invoice_due_date_mode`, which would otherwise produce a past-due
 * half-2 under `fixed_day_of_month` mode.
 */
export function computeHalf2DueDate(params: {
  half1DueDate: string; // YYYY-MM-DD
  offsetDays: number;
}): string {
  const [y, m, d] = params.half1DueDate.split('-').map(Number);
  const due = new Date(y, m - 1, d + params.offsetDays);
  return yyyyMmDd(due);
}

export type SplitFireDecision = {
  fireHalf1: boolean;
  fireHalf2: boolean;
  isRecovery: boolean;
  /**
   * True when a half-2 fell due today for a month that has no half-1, and the
   * month should be billed WHOLE instead (#3637). The caller writes a single
   * no-suffix full-month invoice rather than a lone "(2 of 2)".
   *
   * Never set together with `fireHalf1` / `fireHalf2` — it replaces them for
   * this (athlete, month).
   */
  fireFullMonth: boolean;
  /**
   * True when a half-2 fell due today, there is no live half-1, and the reason
   * is that staff VOIDED it (#3637). Nothing is written at all: voiding the
   * front half of a pair is how a gym cancels the month, so neither a lone
   * "(2 of 2)" nor a resurrected full month is wanted. Counted so the run can
   * report it rather than skip in silence.
   */
  withheldVoidedHalf1: boolean;
};

/**
 * Decide which halves to fire today for a given (team, billing month) pair.
 *
 * Inputs:
 * - `policy`: org's send_on_day, split_enabled, split_offset_days
 * - `orgLocalToday`: today's date in the org's IANA timezone (Y/M/D)
 * - `billingMonth`: the billing month we're deciding for (YYYY-MM)
 * - `half1Exists` / `half2Exists`: whether half invoices already exist
 * - `fullExists`: whether a no-suffix full-month invoice already exists
 *   (gym was on single-send mode for this month, then enabled split). When
 *   true, returns no-fire — writing halves would double-charge the parent.
 *
 * Decision logic:
 * - Half-1: fire only if today is the canonical half-1 date for billingMonth
 *   (today.day === send_on_day AND today's billing month === billingMonth).
 * - Half-2: fire when today equals the canonical half-2 send date for
 *   billingMonth. Half-2 can land in the calendar month AFTER billingMonth
 *   (cross-month attribution).
 * - Half-2 recovery: when half-1 exists, half-2 doesn't, and today is past
 *   the canonical half-2 date but within HALF2_RECOVERY_GRACE_DAYS — fire
 *   half-2 anyway. Caller can log this for operator visibility.
 *
 * When split is disabled OR a full-month invoice already covers the month,
 * returns `{ fireHalf1: false, fireHalf2: false, isRecovery: false }`. The
 * caller falls back to existing single-invoice behavior (or skips entirely).
 */
export function computeSplitFireDecisions(params: {
  policy: {
    auto_invoice_send_on_day: number;
    auto_invoice_split_enabled: boolean;
    auto_invoice_split_offset_days: number;
  };
  orgLocalToday: { year: number; month: number; day: number };
  billingMonth: string; // YYYY-MM
  half1Exists: boolean;
  half2Exists: boolean;
  /**
   * Whether a no-suffix full-month invoice already exists for this
   * (athlete, billingMonth). Forces no-fire so a gym transitioning from
   * single-send to split doesn't get double-charged for the transition month.
   * Optional for backwards compatibility — defaults to false.
   */
  fullExists?: boolean;
  /**
   * Whether a VOIDED half-1 exists for this (athlete, billingMonth). Read
   * separately from `half1Exists`, which counts only live invoices — a voided
   * half-1 is invisible to it, and that invisibility is exactly how a lone
   * "(2 of 2)" reached families whose front half staff had cancelled.
   *
   * Distinguishes "this month was never billed" (bill it whole) from "staff
   * cancelled this month" (bill nothing). Optional; defaults to false, which
   * reproduces the never-billed reading.
   */
  half1Voided?: boolean;
}): SplitFireDecision {
  const { policy, orgLocalToday, billingMonth, half1Exists, half2Exists, fullExists, half1Voided } = params;

  if (!policy.auto_invoice_split_enabled || fullExists) {
    return { fireHalf1: false, fireHalf2: false, isRecovery: false, fireFullMonth: false, withheldVoidedHalf1: false };
  }

  // Canonical half-1 date: send_on_day of billingMonth
  const half1Date = new Date(
    parseBillingMonth(billingMonth).year,
    parseBillingMonth(billingMonth).month - 1,
    policy.auto_invoice_send_on_day,
  );

  // Canonical half-2 date: half-1 date + offset_days (may roll into next month)
  const half2Date = computeHalf2SendDate({
    billingMonth,
    sendOnDay: policy.auto_invoice_send_on_day,
    offsetDays: policy.auto_invoice_split_offset_days,
  });

  const today = new Date(orgLocalToday.year, orgLocalToday.month - 1, orgLocalToday.day);
  const todayMs = today.getTime();
  const half1Ms = half1Date.getTime();
  const half2Ms = half2Date.getTime();

  // Fire half-1: today matches half-1 date AND half-1 doesn't already exist
  const fireHalf1 = todayMs === half1Ms && !half1Exists;

  // Fire half-2 canonical: today matches half-2 date AND half-2 doesn't exist.
  //
  // #3637 — AND a half-1 exists. A "(2 of 2)" with no "(1 of 2)" behind it is
  // never right: the family gets one bill labelled as the back half of a pair
  // whose front half they never received, and the gym collects half the month
  // with the other half billed to nobody. The recovery branch below has always
  // required `half1Exists`; the canonical branch was handed the same flag and
  // ignored it.
  //
  // Reachable whenever the half-1 send day passed without this cron writing one
  // — a gym switching automatic billing on after its own send day, an athlete
  // added to the roster mid-month, or a run that skipped the send day.
  //
  // What replaces the lone half-2 depends on WHY the half-1 is missing, and the
  // two answers are opposites:
  //
  //   never written  -> bill the month WHOLE (`fireFullMonth`). The family owes
  //                     the month and the gym meant to charge it; one no-suffix
  //                     invoice is what should have gone out. It is the same
  //                     money as the pair would have been — `splitLineItems`
  //                     floors half-1 and gives half-2 the remainder, so the two
  //                     halves sum to exactly this full amount by construction.
  //   staff VOIDED it -> write NOTHING (`withheldVoidedHalf1`). Voiding the front
  //                     half is how a gym cancels a month. Re-billing it whole
  //                     would resurrect a charge someone deliberately killed,
  //                     which is worse than the bug this guard exists to fix.
  //
  // Measured in production 2026-08-18: 60 lone "(2 of 2)" invoices had reached
  // families across three months (2026-06 to 2026-08) and two gyms, $7,538.80
  // billed and $6,321.23 already paid. 51 were never-written, 9 were voided-half-1.
  //
  // "Has a half-1" means EXISTS ALREADY or IS BEING WRITTEN BY THIS RUN. The second
  // disjunct is not defensive padding: with `offset_days` 0 the two send dates coincide
  // and one run writes both halves, so by the time half-2 is created its half-1 is there.
  // Reading only `half1Exists` would withhold the half-2 of a perfectly good pair.
  // Production cannot currently produce that shape — `auto_invoice_split_offset_days`
  // carries a CHECK of 1..15 — but this is an exported pure function with no validation of
  // its own, the route's own split tests drive it at 0 deliberately to exercise both guard
  // sites in a single run, and "the database would never" is not a property of a function.
  const half2DueToday = todayMs === half2Ms && !half2Exists;
  const half1Present = half1Exists || fireHalf1;
  const fireHalf2Canonical = half2DueToday && half1Present;

  // Fire half-2 recovery: half-1 exists, half-2 doesn't, today is past
  // canonical half-2 date but within grace window.
  //
  // Counted in whole calendar dates for the same reason as the sibling window
  // in `expandBillingMonthsForSplit` — see `calendarDaysBetween`. Both Dates
  // here are built by the local constructor, so their local parts ARE the
  // calendar dates they mean; reading them back in that frame is safe.
  const daysPastHalf2 = calendarDaysBetween(
    { year: half2Date.getFullYear(), month: half2Date.getMonth() + 1, day: half2Date.getDate() },
    orgLocalToday,
  );
  const fireHalf2Recovery =
    half1Exists &&
    !half2Exists &&
    daysPastHalf2 > 0 &&
    daysPastHalf2 <= HALF2_RECOVERY_GRACE_DAYS;

  // A month that owes a half-2 today but has no half-1 behind it: bill it whole,
  // unless staff voided the half-1, in which case leave it alone entirely.
  const orphanHalf2Today = half2DueToday && !half1Present;

  return {
    fireHalf1,
    fireHalf2: fireHalf2Canonical || fireHalf2Recovery,
    isRecovery: fireHalf2Recovery && !fireHalf2Canonical,
    fireFullMonth: orphanHalf2Today && !half1Voided,
    withheldVoidedHalf1: orphanHalf2Today && !!half1Voided,
  };
}

/**
 * Compute the set of candidate billing months the cron should consider for a
 * team today, accounting for cross-month half-2 attribution.
 *
 * Returns the union of:
 * - `existingMonths` (current month + any catch-up months from
 *   computeBillingMonthsToProcess)
 * - The "previous" billing month if today is within `[half2Date - 0,
 *   half2Date + GRACE_DAYS]` for that month — covers cross-month half-2 and
 *   recovery cases where the previous month's half-2 should fire today.
 *
 * Months are returned in ascending order. When split is disabled, returns
 * `existingMonths` unchanged.
 */
export function expandBillingMonthsForSplit(params: {
  existingMonths: string[];
  policy: {
    auto_invoice_send_on_day: number;
    auto_invoice_split_enabled: boolean;
    auto_invoice_split_offset_days: number;
  };
  orgLocalToday: { year: number; month: number; day: number };
  /**
   * The org's `auto_invoice_enabled_at` timestamp (ISO string). The preferred
   * clamp anchor: the previous billing month is only added when the MONTH of
   * this instant, read in the gym's own zone, is that month or earlier —
   * prevents a phantom half-2 charge for a month the gym could not have been
   * billed for at all. A month, deliberately not a day; the invariant block at
   * the comparison itself says why a day comparison is unsafe here.
   */
  enabledAtDate?: string | null;
  /**
   * The org's `created_at` timestamp (ISO string), used as the clamp anchor
   * when `enabledAtDate` is null. #3464 made gyms be BORN with auto-invoicing
   * on and deliberately left `auto_invoice_enabled_at` unstamped. The settings
   * route writes the stamp only on an OFF→ON transition, which such a gym
   * never makes, so the null is permanent for it and it would otherwise get no
   * clamp at all (#3473).
   *
   * A gym cannot have been billed before it existed, so its creation month is a
   * sound floor. This anchor can only ever REMOVE months from the window,
   * never add them — its one use is an early return that skips the sole
   * `set.add(prevBM)` below.
   *
   * If BOTH anchors are null the gym gets no clamp and #3473 returns for it.
   * `organizations.created_at` is nullable in the catalog (it only DEFAULTs to
   * `now()`), so that is reachable in principle; measured in production
   * 2026-08-17, zero of 76 orgs have a null `created_at`.
   *
   * (`20260819_001` briefly defaulted the stamp to `now()` and `20260820_001`
   * dropped it again; production applied them 28 minutes apart on 2026-08-15,
   * so that default WAS live, just not for long. Measured 2026-08-17: zero orgs
   * were created inside that window. It would not have mattered either way —
   * such a gym's stamp would equal its `created_at`, so both anchors give the
   * same clamp.)
   */
  orgCreatedAt?: string | null;
  /**
   * The gym's IANA timezone. REQUIRED: the clamp below compares the anchor
   * instant against a gym-local calendar date, and there is no safe default —
   * reading the anchor in the process's own zone is exactly the #3614 defect.
   * The cron already resolves this per org for `orgLocalToday`; pass the same
   * value so both sides of the comparison sit in one frame.
   */
  orgTimezone: string;
}): string[] {
  const { existingMonths, policy, orgLocalToday, enabledAtDate, orgCreatedAt, orgTimezone } = params;
  if (!policy.auto_invoice_split_enabled) return existingMonths;

  const set = new Set(existingMonths);
  // Compute the canonical half-2 date for the previous billing month. If today
  // is at-or-past that date but within grace, the previous month belongs in
  // the work list.
  const todayBM = `${orgLocalToday.year}-${String(orgLocalToday.month).padStart(2, '0')}`;
  const prevBM = addMonthsToYyyyMm(todayBM, -1);

  // ---------------------------------------------------------------------
  // THE CLAMP, and the invariant it holds (#3473 / #3614).
  //
  // INVARIANT: `prevBM` may enter the work list only if this gym could have
  // been billed for `prevBM` AT ALL — that is, only if the gym's earliest
  // billable instant, read as a month in the GYM's zone, is `prevBM` or
  // earlier. That instant is its `auto_invoice_enabled_at` stamp, or, for a
  // gym born with auto-invoicing already on and therefore never stamped
  // (#3473), its `created_at`.
  //
  //     prevBM is admissible  <=>  anchorMonth <= prevBM
  //
  // with BOTH sides read in the gym's own zone. That second clause is the
  // whole of #3614: the anchor used to go through `getFullYear()` /
  // `getMonth()` — the PROCESS's zone, UTC on Vercel and the developer's own
  // zone on a laptop — while `prevBM` came from the gym's local today. An
  // anchor of 2026-08-01T02:00:00Z is August in UTC and July 31 in
  // America/New_York, so the same gym got a different clamp depending on where
  // the code ran; measured on a laptop, that instant wrote a phantom "(2 of 2)"
  // that the same code on Vercel clamped away. For a gym EAST of UTC the error
  // runs the other way and admits a month the gym never had.
  //
  // WHY THE ANCHOR IS A MONTH AND NOT A DAY. A tempting tighter rule is
  // "admissible only if the anchor is on or before `send_on_day` of prevBM" —
  // a gym stamped July 8 with send_on_day=25 has no July half-1 from the cron,
  // so July's half-2 looks like a phantom. That rule is WRONG, in the direction
  // that silently loses a gym real money, for two independent reasons:
  //
  //   1. `send_on_day` is mutable and unversioned. The settings route re-stamps
  //      `auto_invoice_enabled_at` only on an OFF->ON transition, so a gym that
  //      billed a half-1 on the 25th and then lowered its send day to the 10th
  //      keeps the old anchor. The day rule would clamp away a month whose
  //      half-1 genuinely exists, and `computeSplitFireDecisions` would never
  //      get the chance to recover it.
  //   2. This cron is NOT the only writer of a "(1 of 2)". The staff-facing
  //      "Generate Monthly Tuition" tool
  //      (`api/admin/billing/invoices/generate-from-division-pricing`) writes
  //      one for an operator-chosen month on any day, ignoring `send_on_day`
  //      entirely — and per the first-run guard's own note, six of fourteen
  //      production gyms with team tuition have hand-billed teams.
  //
  // So the helper cannot know from a date whether a half-1 exists, and must not
  // narrow as if it could. Month granularity is the strongest floor that is
  // actually derivable here: a gym cannot have been billed for a month that
  // ended before it could bill at all.
  //
  // The residual — a month inside the anchor's own month that has no half-1 —
  // is not the clamp's to catch. `computeSplitFireDecisions` fires a canonical
  // half-2 on a date match WITHOUT requiring `half1Exists`, even though it is
  // handed that flag; closing it there would settle the case with the data
  // rather than a guess. Deliberately out of scope for #3614; tracked as #3637,
  // where the read-gap trade-off it carries has to be decided first.
  //
  // Measured against production 2026-08-18 (76 orgs; 22 carry a stamp, 1 has
  // split on): for all 22 stamped gyms the anchor's UTC month and its gym-local
  // month are the SAME month, so moving the anchor into the gym's zone changes
  // no gym's window. The fix is a correctness repair for future gyms and for
  // any gym east of UTC, not a behaviour change for today's.
  //
  // This anchor can still only ever REMOVE months from the window: its one use
  // is the early return below, which skips the sole `set.add(prevBM)`.
  // ---------------------------------------------------------------------
  const clampAnchor = enabledAtDate || orgCreatedAt || null;
  if (clampAnchor) {
    const anchorBM = yyyyMmInTimezone(clampAnchor, orgTimezone);
    // An anchor that does not parse yields the literal month `NaN-NaN`, and
    // every real `prevBM` starts with a digit, which sorts before `N` — so the
    // comparison clamps, which is the recoverable direction (withhold an
    // invoice rather than charge a family off a value we could not read). That
    // is correct by accident of ASCII rather than by design, so it is pinned by
    // a test rather than left to the next reader to rediscover. Not reachable
    // from today's schema either way: both anchor columns are `timestamptz`.
    if (prevBM < anchorBM) {
      return Array.from(set).sort();
    }
  }

  const prevHalf2Date = computeHalf2SendDate({
    billingMonth: prevBM,
    sendOnDay: policy.auto_invoice_send_on_day,
    offsetDays: policy.auto_invoice_split_offset_days,
  });

  // `computeHalf2SendDate` builds its Date with the local constructor, so its
  // local parts are the calendar date it means. Read them back in the same
  // frame and count whole days — see `calendarDaysBetween` for why the old
  // millisecond subtraction was DST-fragile.
  const daysPastPrevHalf2 = calendarDaysBetween(
    {
      year: prevHalf2Date.getFullYear(),
      month: prevHalf2Date.getMonth() + 1,
      day: prevHalf2Date.getDate(),
    },
    orgLocalToday,
  );

  // Include previous month if today is the canonical half-2 day OR within grace.
  if (daysPastPrevHalf2 >= 0 && daysPastPrevHalf2 <= HALF2_RECOVERY_GRACE_DAYS) {
    set.add(prevBM);
  }

  return Array.from(set).sort();
}

/**
 * Halve an array of invoice line items into half-1 or half-2 amounts.
 *
 * Critical invariants:
 * - Both `unit_price` AND `total` are halved symmetrically so the invoice's
 *   `amount === sum(lineItems.total)` validation in BillingService passes.
 * - Floor on half-1, remainder on half-2 — totals always reconcile to the
 *   original full-month amount across both halves.
 * - `quantity` preserved (builder always sets 1; we don't touch it).
 * - `description` gets " (1 of 2)" / " (2 of 2)" suffix so parents instantly
 *   see two invoices per month are expected — not a double-charge.
 * - `metadata` preserved; a `half` field is added.
 */
export function splitLineItems<
  LI extends {
    description: string;
    unit_price: number;
    total: number;
    quantity: number;
    metadata?: Record<string, any> | null;
  },
>(params: {
  lineItems: LI[];
  half: 'first' | 'second';
}): { lineItems: LI[]; subtotal: number } {
  const { lineItems, half } = params;
  const suffix = half === 'first' ? ' (1 of 2)' : ' (2 of 2)';
  const halfNum = half === 'first' ? 1 : 2;

  const halved = lineItems.map((li) => {
    const unitCents = Math.round(li.unit_price * 100);
    const totalCents = Math.round(li.total * 100);
    const half1UnitCents = Math.floor(unitCents / 2);
    const half1TotalCents = Math.floor(totalCents / 2);
    const half2UnitCents = unitCents - half1UnitCents;
    const half2TotalCents = totalCents - half1TotalCents;

    const newUnitCents = half === 'first' ? half1UnitCents : half2UnitCents;
    const newTotalCents = half === 'first' ? half1TotalCents : half2TotalCents;

    return {
      ...li,
      description: `${li.description}${suffix}`,
      unit_price: newUnitCents / 100,
      total: newTotalCents / 100,
      metadata: { ...(li.metadata || {}), half: halfNum },
    };
  });

  // #660 U8: re-apply the engine's $0 floor PER HALF. A ~100% athlete standing discount
  // on an odd-cent base can floor a half a cent below $0 (each line halves independently
  // with floor-on-half-1, so the negative discount line can over-shoot the halved positive
  // lines). `athlete_invoices` has CHECK (total_amount >= 0), so a negative half would FAIL
  // to insert. Trim the negative discount line(s) toward $0 until the half is exactly $0
  // (the half is fully comped in this case, so the cent is immaterial to the family).
  // No-op for any half already >= $0 — i.e. every invoice without a discount line, and any
  // discount < 100%. Only the two division-pricing split paths call this helper.
  let halfTotalCents = halved.reduce((sum, li) => sum + Math.round(li.total * 100), 0);
  if (halfTotalCents < 0) {
    for (const li of halved) {
      if (halfTotalCents >= 0) break;
      const liCents = Math.round(li.total * 100);
      if (liCents >= 0) continue; // only trim negative (discount) lines
      const recover = Math.min(-halfTotalCents, -liCents); // cents to add back toward $0
      const newCents = liCents + recover;
      li.unit_price = newCents / 100;
      li.total = newCents / 100;
      halfTotalCents += recover;
    }
  }

  const subtotal = halved.reduce((sum, li) => sum + li.total, 0);
  return { lineItems: halved, subtotal: Math.round(subtotal * 100) / 100 };
}
